import { useMemo } from 'react';
import { Grid, Stack } from '@mui/material';
import TextField from 'domains/core/components/TextField';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useQueryParams from 'hooks/useQueryParams';

import useSmsData from './hooks/useSmsData';
import useSmsForm from './hooks/useSmsForm';
import useSmsSubmission from './hooks/useSmsSubmission';
import useFormatSmsContent from './hooks/useFormatSmsContent';

import SmsEditor from './components/SmsEditor';
import SmsPreview from './components/SmsPreview';
import SmsHeaderSection from './components/SmsHeaderSection';

import { styles } from './styles';

const ContentSmsBuilderPage = () => {
    // URL params
    const query = useQueryParams();
    const id = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const isCopy = query.get('isCopy') === 'true';

    // Get SMS data without needing content
    const { smsData, isLoading: isSmsLoading, error: smsError, isSmsContent, mergeTags } = useSmsData({ id });

    // Determine component mode
    const isCreate = !isCopy && !isSmsContent;
    const isUpdate = !isCopy && isSmsContent;

    // Calculate initial values for the form
    const initialName = useMemo(() => {
        if (isSmsContent && smsData) {
            return isCopy ? `Copy of ${smsData.name}` : smsData.name || 'Untitled';
        }
        return 'Untitled';
    }, [isSmsContent, isCopy, smsData]);

    const initialContent = useMemo(() => {
        if (isSmsContent && smsData) {
            return smsData.content || '';
        }
        return '';
    }, [isSmsContent, smsData]);

    // Initialize form state
    const {
        name,
        content,
        isNameUndefinedOnTouch,
        isNameTooShort,
        nameHelperText,
        errorMessage,
        setContent,
        setErrorMessage,
        handleChangeName,
    } = useSmsForm({
        initialName,
        initialContent,
    });

    // Format content for preview
    const formattedPreviewContent = useFormatSmsContent(content, mergeTags);

    // API operations
    const { isCreateLoading, isUpdateLoading, createError, updateError, handleSave } = useSmsSubmission({
        id,
        parentFolderId,
        isCreate,
        isCopy,
        setErrorMessage,
    });

    // Save handlers
    const handleSaveName = () => {
        handleSave(name, content, isNameTooShort, isUpdate, 'save');
    };

    const handleSaveAndExit = () => {
        handleSave(name, content, isNameTooShort, isUpdate, 'exit');
    };

    // Loading and error states
    const isLoading = isSmsLoading || isCreateLoading || isUpdateLoading;
    const isError = !!smsError || !!createError || !!updateError || !!errorMessage;

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            {isError && (
                <ErrorSnackbar
                    errorMessage={errorMessage || smsError?.message || createError?.message || updateError?.message}
                />
            )}

            <div style={{ padding: '8px 0' }}>
                <TextField
                    data-testid="name-input"
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={nameHelperText}
                    label="SMS Name"
                    required
                    value={name}
                    width="20%"
                    withClickAway
                    onChange={handleChangeName}
                    onClickAway={handleSaveName}
                />
            </div>

            <SmsHeaderSection content={content} isLoading={isLoading} onSaveAndExit={handleSaveAndExit} />

            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} md={5}>
                    <Stack rowGap={6} sx={styles.container}>
                        <SmsEditor content={content} setContent={setContent} />
                    </Stack>
                </Grid>
                <Grid display="flex" justifyContent="center" item xs={12} md={7}>
                    <SmsPreview formattedContent={formattedPreviewContent} />
                </Grid>
            </Grid>
        </Stack>
    );
};

export default ContentSmsBuilderPage;
